import HistoryIcon from "@mui/icons-material/History";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import {
  DRIVER_DOCUMENT_COMPLIANCE_LABELS_FR,
  DRIVER_DOCUMENT_ISSUE_KINDS,
  type DriverDocumentCompliance,
  type DriverDocumentIssue,
  driverDocumentIssueLabelFr,
} from "@repo/types";
import { formatDateTime } from "@repo/utils/date";
import type { ColumnDef } from "@tanstack/react-table";
import type Driver from "@types_def/models/driver.types";
import { RoutePaths } from "@utils/constants/routes";
import { Link } from "react-router-dom";
import { formatPhoneNumber } from "@/components/common/InputFormat";
import { DriverExpiryPauseBadge } from "@/components/Driver/DriverExpiryPauseBadge";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { TABLE_COLUMN_SIZING } from "@/utils/constants/table-column-sizing";

type TableColumnAlign = "left" | "center" | "right";
type ColumnDefWithAlign<TData> = ColumnDef<TData> & { align?: TableColumnAlign };

export type DriverTableRouteKey =
  | "accepted-requests"
  | "disabled-drivers"
  | "refused-requests"
  | "waiting-requests";

const DOCUMENT_COMPLIANCE_CHIP_COLOR: Record<
  DriverDocumentCompliance,
  "error" | "warning" | "success"
> = {
  EXPIRED: "error",
  MISSING: "error",
  REJECTED: "error",
  EXPIRING: "warning",
  PENDING_REVIEW: "warning",
  DATE_UNVERIFIED: "warning",
  COMPLETE: "success",
};

/** Options for the "documents" server-side filter (URL param). */
export const DRIVER_DOCUMENTS_FILTER_OPTIONS = [
  { label: "Tous", value: "" },
  { label: "Action chauffeur", value: "DRIVER_ACTION" },
  { label: "Action admin", value: "ADMIN_ACTION" },
  ...DRIVER_DOCUMENT_ISSUE_KINDS.map((kind) => ({
    label: DRIVER_DOCUMENT_COMPLIANCE_LABELS_FR[kind],
    value: kind,
  })),
  { label: DRIVER_DOCUMENT_COMPLIANCE_LABELS_FR.COMPLETE, value: "COMPLETE" },
];

function DriverDocumentsCell({
  documentCompliance,
  documentDriverActionRequired,
  documentAdminActionRequired,
  documentIssues,
}: Pick<
  Driver,
  | "documentCompliance"
  | "documentDriverActionRequired"
  | "documentAdminActionRequired"
  | "documentIssues"
>) {
  if (!documentCompliance) return <Typography variant="body2">—</Typography>;

  const actors = [
    documentDriverActionRequired ? "Chauffeur" : null,
    documentAdminActionRequired ? "Admin" : null,
  ].filter(Boolean);

  const tooltip =
    documentIssues.length > 0 ? (
      <Stack spacing={0.5}>
        {documentIssues.map((issue: DriverDocumentIssue) => (
          <Typography key={`${issue.document}-${issue.kind}`} variant="caption">
            {driverDocumentIssueLabelFr(issue)}
          </Typography>
        ))}
      </Stack>
    ) : (
      ""
    );

  return (
    <Tooltip title={tooltip}>
      <Stack spacing={0.25}>
        <Chip
          label={DRIVER_DOCUMENT_COMPLIANCE_LABELS_FR[documentCompliance]}
          color={DOCUMENT_COMPLIANCE_CHIP_COLOR[documentCompliance]}
          size="small"
          variant="outlined"
        />
        {actors.length > 0 ? (
          <Typography variant="caption" color="text.secondary">
            {actors.join(" + ")}
          </Typography>
        ) : null}
      </Stack>
    </Tooltip>
  );
}

function driverDocumentsColumn(): ColumnDefWithAlign<Driver> {
  return {
    id: "documentCompliance",
    enableSorting: false,
    meta: { label: "Documents" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Documents" />,
    cell: ({ row }) => <DriverDocumentsCell {...row.original} />,
  };
}

const DRIVER_TABLE_FILTER_COLUMNS: ColumnDefWithAlign<Driver>[] = [
  {
    id: "email",
    accessorKey: "email",
    enableColumnFilter: true,
    enableHiding: true,
    enableSorting: false,
    meta: {
      variant: "text",
      label: "Email",
      placeholder: "Search by email",
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Email" />,
    cell: ({ row }) => <Typography variant="body2">{row.original.email ?? "-"}</Typography>,
  },
  {
    id: "nameSearch",
    accessorFn: (row) => row.fullName,
    enableColumnFilter: true,
    enableHiding: true,
    enableSorting: false,
    meta: {
      variant: "text",
      label: "Name",
      placeholder: "Search by full name",
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => <Typography variant="body2">{row.original.fullName ?? "-"}</Typography>,
  },
  {
    id: "postalCode",
    accessorFn: (row) => row.address?.postalCode ?? "",
    enableColumnFilter: true,
    enableHiding: true,
    enableSorting: false,
    meta: {
      variant: "text",
      label: "Postal code",
      placeholder: "Search by postal code",
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Postal code" />,
    cell: ({ row }) => (
      <Typography variant="body2">{row.original.address?.postalCode ?? "-"}</Typography>
    ),
  },
  {
    id: "documents",
    // TanStack only filters columns with an accessor; filtering itself is server-side.
    accessorFn: (row) => row.documentCompliance ?? "",
    enableColumnFilter: true,
    enableHiding: true,
    enableSorting: false,
    meta: {
      variant: "select",
      label: "Documents",
      options: DRIVER_DOCUMENTS_FILTER_OPTIONS.filter((option) => option.value !== ""),
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Documents" />,
    cell: () => null,
  },
];

const DRIVER_TABLE_NON_REGISTERED_COLUMNS: ColumnDefWithAlign<Driver>[] = [
  {
    accessorKey: "publicId",
    enableSorting: true,
    meta: { label: "ID" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
    cell: ({ getValue }) => getValue<string>() ?? "",
  },
  {
    accessorKey: "firstName",
    enableSorting: true,
    meta: { label: "Name" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => (
      <a className="main-link" href="#">
        {row.original.firstName} {row.original.lastName}
      </a>
    ),
  },
  {
    accessorKey: "createdAt",
    enableSorting: true,
    meta: { label: "Date de création" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date de création" />,
    cell: ({ row }) => (
      <Typography variant="body2">{formatDateTime(row.original.createdAt)}</Typography>
    ),
  },
  driverDocumentsColumn(),
  {
    id: "action",
    enableSorting: false,
    enableHiding: false,
    meta: { label: "Action", align: "center" },
    size: TABLE_COLUMN_SIZING.sm,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => (
      <Stack alignItems="center" direction="row" gap={0.5} justifyContent="center">
        <Tooltip title="Voir Driver">
          <IconButton
            component={Link}
            to={`${row.original.id}`}
            aria-label="Voir Driver"
            size="small"
            color="primary"
            sx={{ border: 1, borderColor: "divider", boxShadow: 1, "&:hover": { boxShadow: 2 } }}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
  },
];

const DRIVER_TABLE_REGISTERED_COLUMNS: ColumnDefWithAlign<Driver>[] = [
  {
    accessorKey: "publicId",
    enableSorting: false,
    meta: { label: "ID" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="ID" />,
    cell: ({ row }) => <Typography variant="body2">{row.original.publicId ?? ""}</Typography>,
  },
  {
    accessorKey: "firstName",
    enableSorting: true,
    meta: { label: "Name" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
    cell: ({ row }) => (
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <a className="main-link" href="#">
          {row.original.firstName} {row.original.lastName}
        </a>
        <DriverExpiryPauseBadge pausedAt={row.original.documentExpiryPausedAt} />
      </Stack>
    ),
  },
  {
    accessorKey: "phoneNumber",
    enableSorting: false,
    meta: { label: "Numéro" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Numéro" />,
    cell: ({ row }) => (
      <Typography variant="body2">{formatPhoneNumber(row.original.phoneNumber) ?? ""}</Typography>
    ),
  },
  {
    accessorKey: "nbDeals",
    enableSorting: true,
    meta: { label: "Total Deliveries" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Total Deliveries" />,
    cell: ({ row }) => <Typography variant="body2">{row.original.nbDeals ?? 0}</Typography>,
  },
  {
    accessorKey: "createdAt",
    enableSorting: true,
    meta: { label: "Date de création" },
    header: ({ column }) => <DataTableColumnHeader column={column} title="Date de création" />,
    cell: ({ row }) => (
      <Typography variant="body2">{formatDateTime(row.original.createdAt)}</Typography>
    ),
  },
  driverDocumentsColumn(),
  {
    id: "action",
    enableSorting: false,
    enableHiding: false,
    meta: { label: "Action", align: "center" },
    size: TABLE_COLUMN_SIZING.md,
    header: ({ column }) => <DataTableColumnHeader column={column} title="Action" />,
    cell: ({ row }) => {
      const historyRoute =
        row.original.status === "DISABLED" || (!row.original.status && row.original.disabled)
          ? RoutePaths.adminDriversTabledisabledDriversHistory
          : RoutePaths.adminDriversTableacceptedHistory;

      return (
        <Stack direction="row" gap={0.5} justifyContent="center">
          <Tooltip title="Voir Driver">
            <IconButton
              component={Link}
              to={`${row.original.id}`}
              aria-label="Voir Driver"
              size="small"
              color="primary"
              sx={{ border: 1, borderColor: "divider", boxShadow: 1, "&:hover": { boxShadow: 2 } }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="History">
            <IconButton
              component={Link}
              to={`${historyRoute}/${row.original.id}`}
              aria-label="History"
              size="small"
              color="warning"
              sx={{ border: 1, borderColor: "divider", boxShadow: 1, "&:hover": { boxShadow: 2 } }}
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    },
  },
];

export const DRIVER_TABLE_COLUMNS_BY_ROUTE: Record<
  DriverTableRouteKey,
  ColumnDefWithAlign<Driver>[]
> = {
  "accepted-requests": [...DRIVER_TABLE_FILTER_COLUMNS, ...DRIVER_TABLE_REGISTERED_COLUMNS],
  "disabled-drivers": [...DRIVER_TABLE_FILTER_COLUMNS, ...DRIVER_TABLE_REGISTERED_COLUMNS],
  "refused-requests": [...DRIVER_TABLE_FILTER_COLUMNS, ...DRIVER_TABLE_NON_REGISTERED_COLUMNS],
  "waiting-requests": [...DRIVER_TABLE_FILTER_COLUMNS, ...DRIVER_TABLE_NON_REGISTERED_COLUMNS],
};
